import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, Trash2 } from 'lucide-react';
import {
  useChecklistTemplate,
  useDeleteChecklistTemplate,
  useDomains,
  usePatchChecklistTemplate,
} from '@/lib/queries';
import { useAuth } from '@/lib/auth-context';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import ChecklistItemComposer, {
  type ItemDraft,
} from '@/components/ChecklistItemComposer';
import SlugField, { type SlugStatus } from '@/components/SlugField';
import ConfirmDialog from '@/components/ConfirmDialog';
import type { Season } from '@/lib/types';

/**
 * Edit page for an existing checklist template. Mirrors the create
 * wizard's layout — same metadata column, same item composer, same
 * sticky action bar — but starts pre-populated and lands a Save
 * (PATCH) instead of Create (POST). A separate Danger zone below
 * the action bar exposes a destructive delete with a ConfirmDialog
 * and an inline 409 banner when runs reference the template.
 *
 * Guard clauses (rendered as a blank-state empty page):
 *   • template doesn't exist or belongs to another account → 404 message
 *   • template is platform-seeded (account_id NULL) → "read-only"
 *   • caller's role is below `owner` → "not allowed"
 */
export default function ChecklistTemplateEdit() {
  const { idOrSlug } = useParams<{ idOrSlug: string }>();
  const navigate = useNavigate();
  const { account, role } = useAuth();
  const domains = useDomains();
  const q = useChecklistTemplate(idOrSlug);

  const template = q.data?.template ?? null;
  const items = useMemo(() => q.data?.items ?? [], [q.data]);

  // Permission gate.
  const canEdit = role === 'owner' || role === 'admin';
  const isOwnedByActiveAccount =
    template && template.accountId !== null && template.accountId === account?.id;

  // ── form state ─────────────────────────────────────────────
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [slugStatus, setSlugStatus] = useState<SlugStatus>('idle');
  const [domainId, setDomainId] = useState('');
  const [season, setSeason] = useState<Season>('any');
  const [recurrenceRule, setRecurrenceRule] = useState('');
  const [description, setDescription] = useState('');
  const [drafts, setDrafts] = useState<ItemDraft[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [hydrated, setHydrated] = useState(false);

  // Hydrate from the loaded template once. Subsequent re-fetches
  // (from invalidations after save) shouldn't overwrite the user's
  // typing, so we guard with `hydrated`.
  useEffect(() => {
    if (hydrated || !template || items.length === 0) return;
    setTitle(template.title);
    setSlug(template.slug);
    setDomainId(template.domainId);
    setSeason((template.season ?? 'any') as Season);
    setRecurrenceRule(template.recurrenceRule ?? '');
    setDescription(template.description ?? '');
    setDrafts(
      items
        .slice()
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((it) => ({
          tempId: crypto.randomUUID(),
          label: it.label,
          description: it.description,
          required: it.required,
        })),
    );
    setHydrated(true);
  }, [hydrated, template, items]);

  const patch = usePatchChecklistTemplate(template?.id ?? '');
  const del = useDeleteChecklistTemplate();

  const filledItems = useMemo(
    () => drafts.filter((d) => d.label.trim() !== ''),
    [drafts],
  );

  const submitting = patch.isPending;
  const submitDisabled =
    submitting ||
    !title.trim() ||
    !slug ||
    !domainId ||
    filledItems.length === 0 ||
    slugStatus === 'taken' ||
    slugStatus === 'checking';

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    if (!template) return;
    try {
      const updated = await patch.mutateAsync({
        title: title.trim(),
        slug,
        domainId,
        description: description.trim() || null,
        season,
        recurrenceRule: recurrenceRule.trim() || null,
        items: filledItems.map((d) => ({
          label: d.label.trim(),
          description: d.description ?? null,
          required: d.required,
        })),
      });
      navigate(`/checklists/templates/${updated.template.slug}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save.');
    }
  }

  // ── render guards ──────────────────────────────────────────
  if (q.isLoading) {
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  }
  if (q.isError || !template) {
    return <EmptyEditState reason="not-found" />;
  }
  if (!canEdit) {
    return <EmptyEditState reason="role" />;
  }
  if (!isOwnedByActiveAccount) {
    return <EmptyEditState reason="platform" />;
  }

  return (
    <>
      <form
        onSubmit={onSubmit}
        className="pb-[calc(13rem+env(safe-area-inset-bottom))] sm:pb-[calc(11rem+env(safe-area-inset-bottom))] md:pb-[calc(7.5rem+env(safe-area-inset-bottom))] lg:pb-8"
      >
        <header className="space-y-2">
          <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
            <Link to="/checklists" className="hover:text-foreground">
              Checklists
            </Link>{' '}
            ·{' '}
            <Link to="/checklists/templates" className="hover:text-foreground">
              Templates
            </Link>{' '}
            · Edit
          </p>
          <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight">
            Edit template
          </h1>
          <p className="text-muted-foreground max-w-2xl">
            Adjust the steps and metadata. Existing runs keep the snapshot they were
            started with — editing here only affects future runs.
          </p>
        </header>

        <div className="mt-8 grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.5fr)]">
          <section className="space-y-5" aria-labelledby="meta">
            <h2
              id="meta"
              className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground"
            >
              Metadata
            </h2>

            <div className="space-y-1.5">
              <Label
                htmlFor="title-input"
                className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground"
              >
                Title
              </Label>
              <Input
                id="title-input"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="h-12 text-base"
                data-testid="edit-title"
                required
              />
            </div>

            <SlugField
              source={title}
              value={slug}
              onChange={setSlug}
              onStatusChange={setSlugStatus}
              excludeId={template.id}
              initialEdited
            />

            <div className="space-y-1.5">
              <Label
                htmlFor="domain-select"
                className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground"
              >
                Domain
              </Label>
              <select
                id="domain-select"
                value={domainId}
                onChange={(e) => setDomainId(e.target.value)}
                className="h-12 w-full rounded-md border border-input bg-background px-3 text-base focus:outline-none focus:ring-2 focus:ring-ring"
                data-testid="edit-domain"
                required
              >
                <option value="">Pick a domain…</option>
                {(domains.data ?? []).map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label
                  htmlFor="season-select"
                  className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground"
                >
                  Season
                </Label>
                <select
                  id="season-select"
                  value={season}
                  onChange={(e) => setSeason(e.target.value as Season)}
                  className="h-12 w-full rounded-md border border-input bg-background px-3 text-base focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="any">any</option>
                  <option value="spring">spring</option>
                  <option value="summer">summer</option>
                  <option value="fall">fall</option>
                  <option value="winter">winter</option>
                </select>
              </div>
              <div className="space-y-1.5">
                <Label
                  htmlFor="recurrence-input"
                  className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground"
                >
                  Recurrence
                </Label>
                <Input
                  id="recurrence-input"
                  value={recurrenceRule}
                  onChange={(e) => setRecurrenceRule(e.target.value)}
                  placeholder="annual"
                  className="h-12"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label
                htmlFor="description-input"
                className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground"
              >
                Description
              </Label>
              <textarea
                id="description-input"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-ring resize-y"
              />
            </div>
          </section>

          <section className="space-y-3" aria-labelledby="items">
            <h2
              id="items"
              className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground"
            >
              Items
            </h2>
            <p className="text-sm text-muted-foreground">
              Drag the handle to reorder. Tap <span className="font-mono">opt</span> to mark a
              step required.
            </p>
            <ChecklistItemComposer value={drafts} onChange={setDrafts} />
          </section>
        </div>

        {error ? (
          <div className="mt-6 rounded-md border border-destructive/40 bg-destructive/5 px-4 py-3">
            <p className="text-sm text-destructive">{error}</p>
          </div>
        ) : null}

        {/* Sticky action bar — matches the create wizard. */}
        <div
          className={
            'fixed inset-x-0 z-30 border-t border-rule bg-background/95 ' +
            'shadow-[0_-12px_24px_-12px_rgb(0_0_0_/_0.12)] backdrop-blur ' +
            'bottom-[calc(3.5rem+env(safe-area-inset-bottom))] ' +
            'md:bottom-0 md:pb-[env(safe-area-inset-bottom)] md:shadow-none ' +
            'lg:static lg:mt-8 lg:border-0 lg:bg-transparent lg:backdrop-blur-0 lg:pb-0 lg:shadow-none'
          }
        >
          <div className="mx-auto flex max-w-5xl flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-end lg:px-0">
            <div className="flex items-center gap-2 sm:justify-end">
              <Button
                type="button"
                variant="ghost"
                onClick={() => navigate('/checklists/templates')}
                className="h-12 px-3 text-muted-foreground hover:text-foreground"
                data-testid="edit-cancel"
              >
                <ChevronLeft className="mr-1 h-4 w-4" aria-hidden /> Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitDisabled}
                className="h-12 flex-1 sm:flex-initial sm:px-6"
                data-testid="edit-submit"
              >
                {submitting ? 'Saving…' : 'Save changes'}
              </Button>
            </div>
          </div>
        </div>

        {/* Danger zone — sits in flow below the (lg-static) action
            bar, well below the (mobile-fixed) action bar so it can't
            be tapped during a save. */}
        <section
          className="mt-12 lg:mt-8 rounded-md border border-destructive/30 bg-destructive/[0.03] p-4"
          aria-labelledby="danger-zone"
        >
          <h2
            id="danger-zone"
            className="font-mono text-[10px] uppercase tracking-[0.18em] text-destructive mb-2"
          >
            Danger zone
          </h2>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <p className="font-display text-base">Delete this template</p>
              <p className="text-sm text-muted-foreground">
                Permanently removes the template + its items. Existing runs are kept (the
                template can't be deleted while runs reference it).
              </p>
              {deleteError ? (
                <p className="text-sm text-warning font-medium pt-2" role="alert">
                  {deleteError}
                </p>
              ) : null}
            </div>
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setDeleteError(null);
                setDeleteOpen(true);
              }}
              className="shrink-0 text-destructive hover:bg-destructive/10 hover:text-destructive"
              data-testid="edit-delete"
            >
              <Trash2 className="h-4 w-4 mr-2" aria-hidden /> Delete
            </Button>
          </div>
        </section>
      </form>

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Delete this template?"
        description="The template and its items go. Existing runs that reference this template prevent deletion — abandon those runs first if you want a clean slate."
        confirmLabel="Delete template"
        busyLabel="Deleting…"
        tone="destructive"
        testId="confirm-delete-template"
        onConfirm={async () => {
          if (!template) return;
          try {
            await del.mutateAsync(template.id);
            setDeleteOpen(false);
            navigate('/checklists/templates');
          } catch (err) {
            setDeleteError(
              err instanceof Error ? err.message : 'Could not delete template',
            );
            setDeleteOpen(false);
          }
        }}
      />
    </>
  );
}

function EmptyEditState({ reason }: { reason: 'not-found' | 'role' | 'platform' }) {
  const copy =
    reason === 'not-found'
      ? "We couldn't find that template in your account."
      : reason === 'role'
        ? 'Editing templates is restricted to owners and admins on this account.'
        : "This is a platform template and can't be edited. Create a new template if you want a custom version.";
  return (
    <div className="space-y-4 max-w-md">
      <h1 className="font-display text-2xl font-semibold tracking-tight">Can't edit</h1>
      <p className="text-muted-foreground">{copy}</p>
      <Link
        to="/checklists/templates"
        className="inline-flex items-center gap-1 font-mono text-[11px] uppercase tracking-wider text-primary hover:underline"
      >
        <ChevronLeft className="h-3.5 w-3.5" aria-hidden /> Back to templates
      </Link>
    </div>
  );
}
